import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { gql, useMutation, useQuery } from "urql";
import { Link, useSearchParams } from "wouter";

import { Button } from "../../../../../components/src/Button";
import { Alert, AlertVariant } from "../../../../../components/src/Alert";
import { OpConsentStatus, RedirectLinkInfo, OpConsent } from "../../../types.generated";
import { OP_CONSENTS_QUERY } from "../../../../../graphql/src/opconsent/queries";
import { OPItemContext } from "../../../contexts/OPItemContext";
import { ReactTable } from "../../../../../components/src/react-table/ReactTable";
import { YesNoModal } from "../../../../../components/src/YesNoModal";
import { SelectColumnFilter } from "../../../../../components/src/react-table/ReactTableFilters";

const GET_CLIENTS = gql`
    query clients {
        parties(filter: { typeIn: Client }) {
            _id
            name
            legalNumber
            instruments {
                _id
                bic
            }
        }
    }
`;

const GET_REDIRECT_LINK = gql`
    mutation opGetRedirectLink($bic: String!, $clientId: GraphQLObjectId!, $legalNumber: String!) {
        opGetRedirectLink(bic: $bic, clientId: $clientId, legalNumber: $legalNumber) {
            link
            consentId
            consentAuthId
        }
    }
`;

const DELETE_OP_CONSENT = gql`
    mutation opDeleteConsent($consentId: String!) {
        opDeleteConsent(consentId: $consentId)
    }
`;

const UPDATE_OP_CONSENT = gql`
    mutation opUpdateConsent($consentId: String!) {
        opUpdateConsent(consentId: $consentId) {
            _id
        }
    }
`;

const CREATE_OP_TOKEN = gql`
    mutation opCreateToken($code: String!, $scope: String!, $consentId: String!, $consentAuthId: String!, $legalNumber: String!) {
        opCreateToken(code: $code, scope: $scope, consentId: $consentId, consentAuthId: $consentAuthId, legalNumber: $legalNumber) {
            bic
            updateUserId
            updateUserInfo {
                name
            }
        }
    }
`;

export function OpenPaymentsConsentsPage(): React.ReactElement {
    const [{ fetching, data, error }] = useQuery({
        query: GET_CLIENTS
    });

    const [{ fetching: fetchingOPConsents, data: dataOPConsents, error: errorOPConsents }, refetch] = useQuery({
        query: OP_CONSENTS_QUERY
    });
    const [_, opGetRedirectLink] = useMutation(GET_REDIRECT_LINK);
    const [___, opDeleteConsent] = useMutation(DELETE_OP_CONSENT);
    const [__, opCreateToken] = useMutation(CREATE_OP_TOKEN);
    const [____, opUpdateConsent] = useMutation(UPDATE_OP_CONSENT);
    const [selectedBicAndClient, setSelectedBicAndClient] = useState<{
        key: string;
        bic: string;
        clientId: string;
        clientName: string;
        legalNumber: string;
    }>({
        key: "",
        bic: "",
        clientId: "",
        clientName: "",
        legalNumber: ""
    });

    const [queryArgs, setQueryArgs] = useSearchParams();

    const [modal, setModal] = useState({ showModal: false, payload: null });

    const { opItem: opConsent, setOPItem: setOPConsent } = useContext(OPItemContext);

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({
        color: "info",
        visible: false,
        message: ""
    });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    const bicAndClients: { key: string; bic: string; clientId: string; clientName: string; legalNumber: string }[] = useMemo(() => {
        if (!data) return [];

        const bicAndClients: Record<string, { key: string; bic: string; clientId: string; clientName: string; legalNumber: string }> = {};
        for (const party of data.parties) {
            for (const instrument of party.instruments) {
                if (instrument.bic && party.legalNumber) {
                    const key = `${instrument.bic}:${party._id}:${party.legalNumber}`;
                    bicAndClients[key] = {
                        key: key,
                        bic: instrument.bic,
                        clientId: party._id,
                        clientName: party.name,
                        legalNumber: party.legalNumber
                    };
                }
            }
        }
        return Object.values(bicAndClients);
    }, [data]);

    const handleAction = () => {
        if (selectedBicAndClient) {
            let redirectLinkData: RedirectLinkInfo = null;
            opGetRedirectLink({
                bic: selectedBicAndClient.bic,
                clientId: selectedBicAndClient.clientId,
                legalNumber: selectedBicAndClient.legalNumber
            })
                .then((result) => {
                    if ("error" in result && result.error) {
                        setAlert({ color: "danger", visible: true, message: result.error.toString() });
                    } else {
                        console.log("🔍 Redirect link data:", result.data);
                        redirectLinkData = result.data.opGetRedirectLink;
                        setOPConsent({
                            itemId: redirectLinkData.consentId,
                            itemAuthId: redirectLinkData.consentAuthId,
                            bic: selectedBicAndClient.bic,
                            clientId: selectedBicAndClient.clientId,
                            legalNumber: selectedBicAndClient.legalNumber
                        });
                        window.open(redirectLinkData.link);
                    }
                    return true;
                })
                .catch((error) => {
                    setAlert({ color: "danger", visible: true, message: error.toString() });
                });
        }
    };

    const tokenCreated = useRef(false);

    useEffect(() => {
        const consentId = opConsent?.itemId;
        const consentAuthId = opConsent?.itemAuthId;
        const code = queryArgs.get("code");
        const scope = queryArgs.get("scope");

        console.log("🔍 Effect Triggered");
        console.log("  tokenCreated.current:", tokenCreated.current);
        console.log("  consentId:", consentId);
        console.log("  consentAuthId:", consentAuthId);
        console.log("  code:", code);
        console.log("  scope:", scope);

        // Step-by-step conditional breakdown for debugging
        if (tokenCreated.current) {
            console.log("⛔ Skipping: Token already created.");
            return;
        }

        if (!consentId) {
            console.log("⛔ Skipping: Missing consentId.");
            return;
        }

        if (!consentAuthId) {
            console.log("⛔ Skipping: Missing consentAuthId.");
            return;
        }

        if (!code) {
            console.log("⛔ Skipping: Missing code from query args.");
            return;
        }

        if (!scope) {
            console.log("⛔ Skipping: Missing scope from query args.");
            return;
        }

        if (!opConsent.legalNumber) {
            console.log("⛔ Skipping: Missing legalNumber from opConsent.");
            return;
        }

        // ✅ All validations passed — set tokenCreated and run
        tokenCreated.current = true;
        console.log("✅ All inputs present — creating token");

        opCreateToken({ consentId, consentAuthId, code, scope, legalNumber: opConsent.legalNumber })
            .then((result) => {
                if ("error" in result && result.error) {
                    console.error("❌ Token creation error:", result.error);
                    setAlert({ color: "danger", visible: true, message: result.error.toString() });
                }
            })
            .catch((error) => {
                console.error("❌ Token creation exception:", error);
                setAlert({ color: "danger", visible: true, message: error.toString() });
            })
            .finally(() => {
                console.log("🔚 Token creation complete — cleaning up");
                setOPConsent({ itemId: null, itemAuthId: null, bic: "", clientId: "", legalNumber: "" });
                refetch({ requestPolicy: "network-only" });
                setQueryArgs({}, { replace: true });
            });
    }, [opConsent, queryArgs, opCreateToken, setOPConsent, refetch, setQueryArgs]);

    const columns = useMemo(
        () => [
            {
                header: "Id",
                accessorKey: "_id",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/openpayments/consents/${row.original._id}`}>#</Link>;
                },
                size: 15
            },
            {
                header: "Client",
                id: "client.name",
                accessorKey: "client.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return <Link to={`/parties/${row.original.clientId}`}>{cellProps.getValue()}</Link>;
                },
                filter: SelectColumnFilter,
                size: 110
            },
            {
                header: "BIC",
                accessorKey: "bic"
            },
            {
                header: "Consent id",
                accessorKey: "consentId"
            },
            {
                header: "Valid until",
                accessorKey: "validUntil"
            },
            {
                header: "Status",
                id: "consentStatus",
                accessorKey: "consentStatus",
                filter: SelectColumnFilter,
                size: 45
            },
            {
                header: "Delete",
                id: "deleteAction",
                accessorKey: "consentStatus",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.consentStatus === OpConsentStatus.deleted) {
                        return <div style={{ textAlign: "center", width: "100%" }}>-</div>;
                    } else {
                        return (
                            <div>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => {
                                        setModal({
                                            showModal: true,
                                            payload: { _id: row.original._id, consentId: row.original.consentId }
                                        });
                                    }}
                                >
                                    Delete
                                </Button>
                            </div>
                        );
                    }
                },
                size: 30
            },
            {
                header: "Refetch",
                id: "refetchAction",
                accessorKey: "consentStatus",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.consentStatus === OpConsentStatus.deleted) {
                        return <div style={{ textAlign: "center", width: "100%" }}>-</div>;
                    } else {
                        return (
                            <div>
                                <Button
                                    variant="warning"
                                    size="sm"
                                    onClick={() => {
                                        opUpdateConsent({ consentId: row.original.consentId })
                                            .then((result) => {
                                                if ("error" in result && result.error) {
                                                    console.error("❌ Update consent error:", result.error);
                                                    setAlert({
                                                        color: "danger",
                                                        visible: true,
                                                        message: result.error.toString()
                                                    });
                                                }
                                            })
                                            .catch((error) => {
                                                console.error("❌ Update consent exception:", error);
                                                setAlert({ color: "danger", visible: true, message: error.toString() });
                                            })
                                            .finally(() => {
                                                console.log("🔚 Update consent complete — cleaning up");
                                                refetch({ requestPolicy: "network-only" });
                                            });
                                    }}
                                >
                                    Refetch
                                </Button>
                            </div>
                        );
                    }
                },
                size: 30
            },
            {
                header: "Create user",
                accessorKey: "createUserId",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return row.original.createUserInfo ? row.original.createUserInfo.name : "";
                }
            },
            {
                header: "Update user",
                accessorKey: "updateUserId",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return row.original.updateUserInfo ? row.original.updateUserInfo.name : "";
                }
            },
            {
                header: "Create timestamp",
                accessorKey: "createTimestamp",
                size: 80
            },
            {
                header: "Update timestamp",
                accessorKey: "updateTimestamp",
                size: 80
            }
        ],
        [setModal, opUpdateConsent, setAlert, refetch]
    );

    if (fetching || fetchingOPConsents) return <p>Loading</p>;
    if (error) return <pre>error: {JSON.stringify(error, null, 2)}</pre>;
    if (errorOPConsents) return <pre>errorOPConsents: {JSON.stringify(errorOPConsents, null, 2)}</pre>;

    const consents: OpConsent[] = dataOPConsents && dataOPConsents.opConsents ? dataOPConsents.opConsents : [];

    const sortedConsents = consents.slice().sort((a, b) => {
        // 1. Prioritize 'valid' status
        if (a.consentStatus === "valid" && b.consentStatus !== "valid") return -1;
        if (a.consentStatus !== "valid" && b.consentStatus === "valid") return 1;

        // 2. Then sort by updateTimestamp descending
        const aTime = new Date(a.updateTimestamp).getTime();
        const bTime = new Date(b.updateTimestamp).getTime();
        return bTime - aTime; // most recent first
    });

    return (
        <div>
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete consent with _id " + modal.payload._id + "? It cannot be undone."}
                    yesText={"Delete"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        opDeleteConsent({ consentId: modal.payload.consentId })
                            .then((result) => {
                                if ("error" in result && result.error) {
                                    console.error("❌ Delete consent error:", result.error);
                                    setAlert({
                                        color: "danger",
                                        visible: true,
                                        message: result.error.toString()
                                    });
                                }
                            })
                            .catch((error) => {
                                console.error("❌ Delete consent exception:", error);
                                setAlert({ color: "danger", visible: true, message: error.toString() });
                            })
                            .finally(() => {
                                console.log("🔚 Delete consent complete — refetching");
                                refetch({ requestPolicy: "network-only" });
                            });
                    }}
                />
            ) : null}
            <div className="flex flex-wrap -mx-2 px-4">
                <div className="w-full px-2 mb-4">
                    <Link to="/openpayments/payments">Payments</Link>
                </div>
            </div>
            <div>
                <div className="flex flex-wrap -mx-2 px-4">
                    <div className="w-full px-2 mb-4 min-w-0">
                        <div className="mb-3">
                            <label htmlFor="open-payments-bic-select" className="block font-bold mb-1">
                                BIC
                            </label>
                            <select
                                id="open-payments-bic-select"
                                value={selectedBicAndClient.key}
                                onChange={(e) => {
                                    const selectedKey = e.target.value;
                                    const selected = bicAndClients.find((bic) => bic.key === selectedKey);
                                    setSelectedBicAndClient(
                                        selected || {
                                            key: "",
                                            bic: "",
                                            clientId: "",
                                            clientName: "",
                                            legalNumber: ""
                                        }
                                    );
                                }}
                                className="w-full min-w-0 max-w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="">-- Choose a BIC --</option>
                                {bicAndClients
                                    .slice() // create a shallow copy to avoid mutating the original array
                                    .sort((a, b) => {
                                        const aString = `${a.bic} - ${a.clientName} - ${a.legalNumber}`;
                                        const bString = `${b.bic} - ${b.clientName} - ${b.legalNumber}`;
                                        return aString.localeCompare(bString);
                                    })
                                    .map((bic) => (
                                        <option key={bic.key} value={bic.key}>
                                            {bic.bic} - {bic.clientName} - {bic.legalNumber}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap -mx-2 px-4">
                    <div className="w-full px-2 mb-4 min-w-0">
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handleAction}
                            disabled={!selectedBicAndClient}
                            className="w-full max-w-full text-left whitespace-normal"
                        >
                            Authorize system, through PSD2 for {selectedBicAndClient.bic} - {selectedBicAndClient.clientName}, to access
                            accounts, transactions and initiate payments
                        </Button>
                    </div>
                </div>
            </div>
            <div className="flex flex-wrap -mx-2 mt-4 px-4">
                <div className="w-full px-2 mb-4">
                    {alert.visible ? (
                        <Alert variant={alert.color} onClose={onDismissAlert} dismissible>
                            {alert.message}
                        </Alert>
                    ) : null}
                </div>
            </div>
            <div className="flex flex-wrap -mx-2 mt-4 px-4">
                <div className="w-full px-2 mb-4">
                    <ReactTable columns={columns} data={sortedConsents} defaultHiddenColumns={[]} />
                </div>
            </div>
        </div>
    );
}
